import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import {
  type LastInteractedStore,
  useLastInteractedStoreMenuHref,
} from '../../../../../../context/LastInteractedStore';
import {
  useScreenHeaderAddressQuery,
  useScreenHeaderRestaurantQuery,
} from '../../../../../ScreenHeader/graphql/ScreenHeader.generated';
import { navbarV2Messages as messages } from '../../../../NavbarV2.messages';

// ─────────────────────────────────────────────────────────────────────────────

export const useNavbarLocationData = (params: Params) => {
  const { lastInteractedStore } = params;

  const { formatMessage } = useIntl();
  const menuHref = useLastInteractedStoreMenuHref();

  // ─────────────────────────────────────────────────────────────────────

  const addressId = lastInteractedStore?.addressId ?? '';
  const restaurantId = lastInteractedStore?.restaurantId ?? '';

  // ─── Remote Data ─────────────────────────────────────────────────────

  const [{ data: addressData }] = useScreenHeaderAddressQuery({
    variables: { addressId },
    pause: !addressId,
    requestPolicy: 'cache-first',
  });

  const [{ data: restaurantData }] = useScreenHeaderRestaurantQuery({
    variables: { id: restaurantId },
    pause: !restaurantId,
    requestPolicy: 'cache-first',
  });

  // ─── Derived ─────────────────────────────────────────────────────────

  const addressName = addressData?.address?.name;
  const restaurant = restaurantData?.restaurant;

  const orderChannel = useMemo(() => {
    if (addressId) return 'delivery';
    if (restaurant?.isOutpost) return 'outpost';

    return 'pickup';
  }, [addressId, restaurant?.isOutpost]);

  const locationName = addressId ? addressName : restaurant?.name;
  const locationTitle = locationName ?? formatMessage(messages.findLocationNearYou);

  const locationEyebrow = lastInteractedStore
    ? formatMessage(messages.locationEyebrow, { orderChannel })
    : undefined;

  // ─────────────────────────────────────────────────────────────────────

  return { locationEyebrow, locationTitle, menuHref };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  lastInteractedStore: LastInteractedStore;
};
